import environment from '@department-of-veterans-affairs/platform-utilities/environment';
import {
  AUTH_PARAMS as USIP_QUERY_PARAMS,
  EXTERNAL_APPS as USIP_APPLICATIONS,
} from 'platform/user/authentication/constants';

import { externalApplicationsConfig } from 'platform/user/authentication/usip-config';

import {
  API_SIGN_IN_SERVICE_URL as SIS_API_URL,
  OAUTH_KEYS as SIS_QUERY_PARAM_KEYS,
} from '~/platform/utilities/oauth/constants';

const ARP_SIGN_IN_URL = '/representative/sign-in';
const USIP_BASE_URL = environment.BASE_URL;

export const getSignInUrl = ({ returnUrl } = {}) => {
  const signInPath = ARP_SIGN_IN_URL;
  const url = new URL(signInPath, USIP_BASE_URL);
  url.searchParams.set(USIP_QUERY_PARAMS.application, USIP_APPLICATIONS.ARP);
  url.searchParams.set(USIP_QUERY_PARAMS.OAuth, true);
  if (returnUrl) {
    url.searchParams.set(USIP_QUERY_PARAMS.to, returnUrl);
  }
  return url;
};

export const SIGN_OUT_URL = (() => {
  const url = new URL(SIS_API_URL({ endpoint: 'logout' }));
  url.searchParams.set(
    SIS_QUERY_PARAM_KEYS.CLIENT_ID,
    externalApplicationsConfig[USIP_APPLICATIONS.ARP].oAuthOptions.clientId,
  );

  return url;
})();

export const PROFILE_MENU_LINKS = [
  {
    LABEL: 'Dashboard',
    URL: '/representative/dashboard',
    TEST_ID: 'user-nav-profile-link',
    EVENT_NAME: 'nav-link-click',
  },
  {
    LABEL: 'Sign Out',
    URL: SIGN_OUT_URL,
    TEST_ID: 'user-nav-sign-out-link',
    EVENT_NAME: 'nav-header-sign-out',
  },
];

export const HAMBURGER_MENU_LINKS = [
  {
    LABEL: 'Find Claimant',
    URL: '/representative/find-claimant',
    TEST_ID: 'user-nav-claimant-search-link',
  },
  {
    LABEL: 'Representation Requests',
    URL: '/representative/representation-requests',
    TEST_ID: 'user-nav-representation-requests-link',
  },
  {
    LABEL: 'Submissions',
    URL: '/representative/submissions',
    TEST_ID: 'user-nav-submissions-link',
  },
];

export const HAMBURGER_MENU_LINKS_UPDATED = [
  {
    LABEL: 'Dashboard',
    URL: '/representative/dashboard',
    TEST_ID: 'user-nav-dashboard-link',
  },
  {
    LABEL: 'Establish Representation',
    URL: '/representative/representation-requests',
    TEST_ID: 'user-nav-representation-requests-link',
  },
  {
    LABEL: 'Find Your Claimants',
    URL: '/representative/find-claimant',
    TEST_ID: 'user-nav-claimant-search-link',
  },
  {
    LABEL: 'Submissions',
    TEST_ID: 'user-nav-submissions-button',
    LINKS: [
      {
        LABEL: 'Submit forms and files',
        URL: '/representative/submit-forms-and-files',
        TEST_ID: 'user-nav-submit-forms-and-files',
      },
      {
        LABEL: 'In-progress forms',
        URL: '/representative/in-progress-forms',
        TEST_ID: 'user-nav-in-progress-forms',
        FEATURE_FLAG_NAME: 'accreditedRepresentativePortalSubmit686cV2',
      },
      {
        LABEL: 'Submission history',
        URL: '/representative/submission-history',
        TEST_ID: 'user-nav-submission-history',
      },
      {
        LABEL:
          'Start VA Form 21-686c and 21-674 (Add and/or Remove Dependents)',
        URL: '/representative/representative-form-upload/va-form-21-686c-674/introduction',
        TEST_ID: 'user-nav-21-686c-674',
        FEATURE_FLAG_NAME: 'accreditedRepresentativePortalSubmit686cV2',
      },
      {
        LABEL: 'Start VA Form 21-0966 (Intent to File)',
        URL: '/representative/representative-form-upload/submit-va-form-21-0966/introduction',
        TEST_ID: 'user-nav-21-0966',
      },
      {
        LABEL: 'Upload VA Form 21-526ez (Disability claim)',
        URL: '/representative/representative-form-upload/submit-va-form-21-526ez/introduction',
        TEST_ID: 'user-nav-21-526ez',
      },
      {
        LABEL: 'Upload VA Form 21-686c (Add and/or Remove Dependents)',
        URL: '/representative/representative-form-upload/submit-va-form-21-686c/introduction',
        TEST_ID: 'user-nav-21-686c',
        FEATURE_FLAG_NAME: 'accreditedRepresentativePortalSubmit686cV2',
        DISPLAY_WHEN_DISABLED: true,
      },
      {
        LABEL: 'Upload and submit other forms',
        URL: '/representative/representative-form-upload/submit-other-forms/introduction',
        TEST_ID: 'user-nav-submit-other-forms',
      },
      {
        LABEL: 'Upload with QuickSubmit',
        URL: '/representative/submit-forms-and-files/upload',
        TEST_ID: 'user-nav-quick-submit',
        FEATURE_FLAG_NAME: 'accreditedRepresentativePortalCdUpdateQsLink',
      },
    ],
  },
];

export const NAV_DESKTOP = [
  {
    LABEL: 'Find Claimant',
    URL: '/representative/find-claimant',
    TEST_ID: 'desktop-search-link',
  },
  {
    LABEL: 'Representation Requests',
    URL: '/representative/representation-requests',
    TEST_ID: 'desktop-poa-link',
  },
  {
    LABEL: 'Submissions',
    URL: '/representative/submissions',
    TEST_ID: 'submissions-link',
  },
];

export const NAV_DESKTOP_UPDATED = [
  {
    LABEL: 'Dashboard',
    URL: '/representative/dashboard',
    TEST_ID: 'desktop-dashboard-link',
  },
  {
    LABEL: 'Establish Representation',
    URL: '/representative/representation-requests',
    TEST_ID: 'desktop-poa-link',
  },
  {
    LABEL: 'Find Your Claimants',
    URL: '/representative/find-claimant',
    TEST_ID: 'desktop-find-claimant-link',
  },
  {
    LABEL: 'Submissions',
    URL: '/representative/submissions',
    TEST_ID: 'desktop-submissions-link',
    SECTIONS: [
      {
        LINKS: [
          {
            LABEL: 'Submit forms and files',
            URL: '/representative/submit-forms-and-files',
            TEST_ID: 'desktop-submit-forms-and-files',
          },
          {
            LABEL: 'In-progress forms',
            URL: '/representative/in-progress-forms',
            TEST_ID: 'desktop-in-progress-forms',
            FEATURE_FLAG_NAME: 'accreditedRepresentativePortalSubmit686cV2',
          },
          {
            LABEL: 'Submission history',
            URL: '/representative/submission-history',
            TEST_ID: 'desktop-submission-history',
          },
        ],
      },
      {
        LABEL: 'Submit online forms for fastest processing',
        LINKS: [
          {
            LABEL:
              'Start VA Form 21-686c and 21-674 (Add and/or Remove Dependents)',
            URL: '/representative/representative-form-upload/va-form-21-686c-674/introduction',
            TEST_ID: 'desktop-21-686c-674',
            FEATURE_FLAG_NAME: 'accreditedRepresentativePortalSubmit686cV2',
          },
          {
            LABEL: 'Start VA Form 21-0966 (Intent to File)',
            URL: '/representative/representative-form-upload/submit-va-form-21-0966/introduction',
            TEST_ID: 'desktop-21-0966',
          },
        ],
      },
      {
        LABEL: 'Submit other forms',
        LINKS: [
          {
            LABEL: 'Upload VA Form 21-526ez (Disability claim)',
            URL: '/representative/representative-form-upload/submit-va-form-21-526ez/introduction',
            TEST_ID: 'desktop-21-526ez',
          },
          {
            LABEL: 'Upload VA Form 21-686c (Add and/or Remove Dependents)',
            URL: '/representative/representative-form-upload/submit-va-form-21-686c/introduction',
            TEST_ID: 'desktop-21-686c',
            FEATURE_FLAG_NAME: 'accreditedRepresentativePortalSubmit686cV2',
            DISPLAY_WHEN_DISABLED: true,
          },
          {
            LABEL: 'Upload and submit other forms',
            URL: '/representative/representative-form-upload/submit-other-forms/introduction',
            TEST_ID: 'desktop-submit-other-forms',
          },
        ],
      },
      {
        LABEL: 'Upload any file with QuickSubmit',
        FEATURE_FLAG_NAME: 'accreditedRepresentativePortalCdUpdateQsLink',
        LINKS: [
          {
            LABEL: 'Upload with QuickSubmit',
            URL: '/representative/submit-forms-and-files/upload',
            TEST_ID: 'desktop-quick-submit',
          },
        ],
      },
    ],
  },
];

export const SUBMIT_OTHER_FORM_LIST = {
  10182: { subTitle: 'Notice of Disagreement' },
  '20-0995': { subTitle: 'Decision Review Request: Supplemental Claim' },
  '20-0996': { subTitle: 'Decision Review Request: Higher-Level Review' },
  '20-10207': { subTitle: 'Request for priority processing' },
  '20-10208': { subTitle: 'Document/Evidence Submission' },
  '21-0304': {
    subTitle:
      "Application for Benefits for a Qualifying Veteran's Child Born with Disabilities",
  },
  '21-0779': {
    subTitle:
      'Request for Nursing Home Information in Connection with Claim for Aid & Attendance',
  },
  '21-0788': {
    subTitle: "Information Regarding Apportionment of Beneficiary's Award",
  },
  '21-2680': {
    subTitle:
      'Examination for Housebound Status or Permanent Need for Regular Aid and Attendance',
  },
  '21-4140': { subTitle: 'Employment Questionnaire' },
  '21-4170': { subTitle: 'Statement of Marital Relationship' },
  '21-4192': {
    subTitle:
      'Request for Employment Information in Connection with Claim for Disability Benefits',
  },
  '21-4193': {
    subTitle:
      'Notice to Department of Veterans Affairs of Veteran or Beneficiary Incarcerated in Penal Institution',
  },
  '21-509': { subTitle: 'Statement of Dependency of Parents' },
  '21-534': {
    subTitle:
      'Application for Dependency and Indemnity Compensation or Death Pension by a Surviving Spouse or Child',
  },
  '21-651': { subTitle: 'Election of Compensation in Lieu of Retired Pay' },
  '21-674b': { subTitle: 'School Attendance Report' },
  '21-8940': {
    subTitle:
      'Veteran’s Application for Increased Compensation Based on Unemployability',
  },
  '21-8951-2': {
    subTitle:
      'Notice of Waiver of VA Compensation or Pension to Receive Military Pay and Allowances',
  },
  '21-8960': { subTitle: 'Certification of School Attendance or Termination' },
  '21P-0516-1': {
    subTitle:
      'Improved Pension Eligibility Verification Report (Veteran with No Children)',
  },
  '21P-0517-1': {
    subTitle:
      'Improved Pension Eligibility Verification Report (Veteran with Children)',
  },
  '21P-0518-1': {
    subTitle:
      'Improved Pension Eligibility Verification Report (Surviving Spouse with No Children)',
  },
  '21P-0519C-1': {
    subTitle:
      'Improved Pension Eligibility Verification Report (Child or Children)',
  },
  '21P-0519S-1': {
    subTitle:
      'Improved Pension Eligibility Verification Report (Surviving Spouse with Children)',
  },
  '21P-4171': { subTitle: 'Supporting Statement Regarding Marriage' },
  '21P-4185': { subTitle: 'Report of Income from Property or Business' },
  '21P-4706b': { subTitle: "VA Fiduciary's Account" },
  '21P-4706c': { subTitle: "Court Appointed Fiduciary's Account" },
  '21P-4718a': {
    subTitle:
      'Certificate of Balance on Deposit and Authorization to Disclose Financial Records',
  },
  '21P-524': {
    subTitle:
      'Statement of person claiming to have stood in relation of parent',
  },
  '21P-527EZ': { subTitle: 'Application for Veterans Pension' },
  '21P-530a': { subTitle: 'Application for Burial Benefits' },
  '21P-535': {
    subTitle:
      'For Application for Dependency and Indemnity Compensation by Parent(s) (Including Accrued Benefits and Death Compensation when Applicable)',
  },
  '21P-601': {
    subTitle: 'Application for Accrued Amounts Due a Deceased Beneficiary',
  },
  '21P-8049': { subTitle: 'Request for Details of Expenses' },
  '21P-8924': {
    subTitle: 'Application of Surviving Spouse or Child for Reps Benefits',
  },
  '26-1880': { subTitle: 'Loan Guaranty Certificate of Eligibility' },
  '28-1900': { subTitle: 'Application for Veteran Readiness & Employment' },
};
